import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { CPModelEstimate, CriticalPowerPoint, WBalPoint } from './usePowerStats'

export interface AnalysisResult {
  grade: 'excellent' | 'partially' | 'poor' | 'not-graded'
  percentage: number
  insights: {
    category?: 'intervals' | 'laps' | 'metrics' | 'heart_rate_zones'
    level: 'significant' | 'moderate'
    description: string
  }[]
  totalWeights: number
  criticalPower?: CriticalPowerPoint[] | null
  cpModel?: CPModelEstimate | null
  wBal?: WBalPoint[] | null
  cpReference?: CPReference | null
}

export interface CPReference {
  dateRange: string[]
  values: CriticalPowerPoint[]
}

export interface UseWorkoutAnalysisReturn {
  analysisResult: AnalysisResult | null
  isLoading: boolean
  error: string | null
  analyseWorkout: (workoutId: string) => Promise<void>
  reset: () => void
}

/**
 * Hook for analyzing workout execution using the analyse-workout edge function
 * Provides execution grade and insights based on workout data
 */
export const useWorkoutAnalysis = (workoutId: string) => {
  const { data, isFetching, isLoading, error } = useQuery<AnalysisResult>({
    queryKey: ['analyse-workout', workoutId],
    enabled: workoutId !== undefined,
    queryFn: async () => {
      console.log('useWorkoutAnalysis', { workoutId })
      const { data, error } = await supabase.functions.invoke('analyse-workout', {
        body: {
          workout_id: workoutId,
        },
      })
      if (error) {
        throw new Error('Failed to perform workout analysis')
      }
      return {
        grade: data.grade,
        percentage: data.percentage || 0,
        insights: data.insights || [],
        totalWeights: data.totalWeights || 0,
        criticalPower: data.criticalPower || null,
        cpModel: data.cpModel || null,
        wBal: data.wBal || null,
        cpReference: data.cpReference || null,
      }
    },
  })
  return {
    data,
    isLoading,
    isFetching,
    error,
  }
}
